import { useEffect } from "react";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import { ECodeInsertionMode, IPlannedCodeInsertion } from "./ai-code-insertion";
import "./ai-pending-edit-bar.scss";

export interface IAiPendingEditBarProps {
  plan: IPlannedCodeInsertion;
  /** What this editor calls its content, e.g. "query" or "code". */
  subject: string;
  onApply: () => void;
  onDiscard: () => void;
  onChangeMode: (mode: ECodeInsertionMode) => void;
}

const lineWord = (count: number): string => (count === 1 ? "line" : "lines");

const describePlan = (plan: IPlannedCodeInsertion, subject: string): string => {
  if (plan.mode === "replace-all") {
    return `AI will replace the whole ${subject} with ${plan.addedLineCount} ${lineWord(plan.addedLineCount)}`;
  }
  if (plan.replacedRange) {
    return `AI will replace the selection with ${plan.addedLineCount} ${lineWord(plan.addedLineCount)}`;
  }
  if (plan.previewAfterLineNumber === 0) {
    return `AI will write ${plan.addedLineCount} ${lineWord(plan.addedLineCount)}`;
  }
  return `AI will add ${plan.addedLineCount} ${lineWord(plan.addedLineCount)} after line ${plan.previewAfterLineNumber}`;
};

/**
 * The gate between an AI answer and the editor's content. It is on screen only while an edit
 * is pending, and while it is up the model still holds exactly what the user wrote.
 */
export const AiPendingEditBar = (props: IAiPendingEditBarProps): JSX.Element => {
  const { plan, subject, onApply, onDiscard, onChangeMode } = props;

  useEffect((): (() => void) => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      // Captured before the host modal sees it, so Escape dismisses the preview, not the editor.
      event.preventDefault();
      event.stopPropagation();
      onDiscard();
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return (): void => document.removeEventListener("keydown", handleKeyDown, true);
  }, [onDiscard]);

  const isReplacingAll: boolean = plan.mode === "replace-all";

  return (
    <div className="kuika__ai-pending-edit-bar" data-testid="aiPendingEditBar" role="status">
      <span className="kuika__ai-pending-edit-bar__text" data-testid="aiPendingEditBarText">
        {describePlan(plan, subject)}
      </span>

      <button
        type="button"
        className="kuika__ai-pending-edit-bar__mode"
        data-testid="aiPendingEditBarModeToggle"
        onClick={(): void => onChangeMode(isReplacingAll ? "at-cursor" : "replace-all")}
      >
        {isReplacingAll ? "Add at cursor instead" : `Replace the whole ${subject} instead`}
      </button>

      <span className="kuika__ai-pending-edit-bar__actions">
        <KuikaButton type="secondary" size="sm" onClick={onDiscard} data-testid="aiPendingEditBarDiscardButton">
          Cancel
        </KuikaButton>
        <KuikaButton size="sm" onClick={onApply} data-testid="aiPendingEditBarApplyButton">
          Apply
        </KuikaButton>
      </span>
    </div>
  );
};
